import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Archive,
  Award,
  BedDouble,
  Boxes,
  Images,
  Paintbrush,
  Sofa,
  Truck,
} from "lucide-react";

export const homeGoodsPrompts: CategoryPrompt[] = [
  {
    icon: <Sofa />,
    title: "Use-Case Landing Pages",
    description:
      "Create a page targeting a specific use case with scale-appropriate products, styling tips, and customer photos from similar spaces.",
    prompt: `**Build a use-case specific landing page that targets a room or scenario with scale-appropriate products, styling guidance, and real customer spaces.**

**USE CASE:** e.g., "Small Apartment Living" OR "Home Office Setup" OR "Nursery Essentials" OR "Outdoor Patio Styling"

**SPACE CONTEXT:** e.g., Square footage, functionality needs, style constraints

**Target Shopper:** Choose one:
- [ ] Space-specific (furnishing particular room)
- [ ] Size-constrained (small spaces, apartments)
- [ ] Lifestyle-driven (work from home, entertaining, families)

**Tone & Style:**
- Practical space planner
- Realistic not aspirational perfection
- Function-first then style
- Relatable to space constraints

**CTA Guidelines:** Each CTA should:
- Reference use case ("Perfect for Small Spaces")
- Use functionality language ("Maximize Your Office")
- Include scale context ("Fits 10x12 Rooms")
- Emphasize suitability ("Made for This")

**Conversion Tactics Use:**
- Room measurements and product fit
- Multi-angle customer space photos
- Scale comparisons (dimensions shown)
- Bundle suggestions for complete room
- Layout templates for space
- Function-specific product features

**Key Elements to Include:**
- **Hero Section:** Use case headline, styled space image, "Shop This Space" CTA
- **The Challenge:** Why this use case needs specific solutions (2-3 paragraphs on constraints)
- **Space Requirements:** Typical dimensions, furniture needs, functionality must-haves
- **Product Collection:** 6-12 items perfect for this use case
- **Why These Work:** Scale, function, and style reasons per product
- **Scale & Dimensions:** Size specs with "fits X sq ft spaces"
- **Room Layout Ideas:** 2-4 floor plan arrangements with products
- **Styling Tips:** How to maximize space/function for this use case
- **Customer Space Gallery (8-15 photos):** Real homes with same use case
- **Complete Room Bundles:** Pre-selected sets for this space with savings
- **Measurement Guide:** How to measure your space before buying
- **Multi-Functional Features:** Products that serve multiple purposes
- **Storage Solutions:** Space-saving options for use case
- **Color/Style Variations:** Options that work in this context
- **Before/After:** Customer spaces transformed
- **Budget Options:** Essential vs. complete vs. luxury packages
- **Assembly/Setup:** Difficulty level and time estimates
- **CTAs:** Hero, with bundles, after gallery, products section (5-6 total)
- **FAQ:** 5-7 questions (Fits my space? Measurements? Assembly? Returns? Smaller options?)

**Goal:** Convert use-case specific shoppers into confident buyers by providing scale-appropriate product recommendations backed by real customer photos from similar spaces that prove functionality and fit, eliminating guesswork and increasing bundle purchases.`,
  },
  {
    icon: <Paintbrush />,
    title: "Problem-Solution Listicle Advertorial",
    description:
      "Build a listicle addressing common problems, showcasing your solutions with research backing and CTAs.",
    prompt: `**Build a problem-solution listicle that identifies common home frustrations and positions your products as the designed solutions.**

**TOPIC:** e.g., "7 Reasons Your Home Feels Cluttered (And How to Fix It)" OR "5 Small Space Mistakes Making Your Home Look Smaller" OR "Why Your Living Room Isn't Working"

**PRODUCTS FEATURED:** e.g., "Our storage ottoman solves #3" OR "Our modular shelving fixes issue #5"

**Reader Type:** Choose one:
- [ ] Space-frustrated (home not functioning well)
- [ ] Organization seekers (need better systems)
- [ ] Style-conscious (want form and function)

**Tone & Style:**
- Empathetic interior design advisor
- Practical problem-solver
- "You're not alone" validation
- Function-focused solutions

**CTA Guidelines:** Each CTA should:
- Reference problem solved ("End the Clutter")
- Use functional benefit ("Maximize Storage")
- Include design element ("Stylish Solution")
- Emphasize transformation ("Fix This Now")

**Conversion Tactics Use:**
- Before/after space transformations
- Space-saving measurements shown
- Multi-functional product benefits
- Customer photos solving same problem
- Design principle explanations
- Bundle suggestions for complete solutions

**Key Elements to Include:**
- **Hook:** Relatable home frustration (2-3 paragraphs on common struggle)
- **List Items (6-8 problems):** Numbered issue → why it happens → your solution
- **Problem Structure Per Item:**
  - Problem headline: "#X: [Common home frustration]"
  - Why this happens: Design principle or space mistake
  - Impact on home: How it affects function/aesthetics
  - The solution: Your product that addresses it
  - How it works: Design features and functionality
  - Space-saving metrics: Storage capacity, footprint, dimensions
  - Styling tip: How to integrate into existing space
  - Customer photo: Real home solving this exact problem
- **Product Callouts:** Design features highlighted with "Shop Solution" CTA
- **Before/After Comparisons:** Customer spaces transformed (2-3 throughout)
- **Design Principles:** Why solutions work (scale, proportion, multi-function)
- **Space Planning Tips:** Broader advice for each problem area
- **Measurement Guidance:** How to assess if solution fits your space
- **Multi-Problem Bundles:** Products that solve several issues together
- **CTAs:** After problem #2, after problem #5, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Fits small spaces? Assembly? Color options? Returns?)

**Goal:** Convert home-frustrated shoppers into confident buyers by validating their common problems and demonstrating how your thoughtfully-designed products provide functional, stylish solutions backed by real customer spaces that prove transformation is achievable.`,
  },
  {
    icon: <BedDouble />,
    title: "Home Aesthetic Quizzes",
    description:
      "Build a style quiz asking about preferences and needs, then recommend furniture products from my catalog.",
    prompt: `**Build a home aesthetic quiz that matches customers to furniture and decor based on style preferences and lifestyle needs.**

**QUIZ FOCUS:** e.g., "Find Your Home Style" OR "What's Your Design Aesthetic?" OR "Your Perfect Furniture Match"

**PRODUCT RANGE:** e.g., Furniture, decor, lighting across styles (20-40 items to match from)

**Quiz Taker Type:** Choose one:
- [ ] Style-undefined (don't know their aesthetic)
- [ ] Room refreshers (updating space, need direction)
- [ ] Overwhelmed shoppers (too many choices)

**Tone & Style:**
- Fun design personality energy
- Affirming and inspiring
- Practical lifestyle context
- "Your home should reflect you"

**Quiz Question Guidelines:** Each question should:
- Use visual style examples or mood images
- Balance aesthetic with lifestyle function
- Build toward specific design categories
- Feel like personality discovery
- 6-8 total questions maximum

**Email Gate Strategy:**
- Place after question #4 (mid-commitment)
- Frame as "Get your personalized design guide"
- Offer incentive: "Unlock style + 15% off curated pieces"
- Single field: email only
- Trust line: "Join 80,000+ who found their style"

**Results Page Elements:**
- **Personalized Headline:** "Your style: [Design Aesthetic] - here's your perfect pieces"
- **Style Profile Badge:** "You're [Modern Minimalist/Cozy Boho/Industrial Chic/etc.]"
- **Style Description:** What defines this aesthetic (color, materials, vibe)
- **Curated Collection:** 8-15 products matching their style
- **Complete Room Looks:** 2-3 fully styled spaces with "Shop This Look" bundles
- **Product Details:** Why each fits their aesthetic + functional benefits
- **Product Cards Include:** Image, price, style notes, dimensions, "Add to Cart"
- **Match Reasoning:** "You said [answer], so this [design element] fits because..."
- **Styling Guide:** How to mix pieces in this aesthetic
- **Color Palette:** Recommended colors for their style
- **Material Guide:** Textures and finishes that work
- **Budget Options:** Entry vs. investment pieces in style
- **Mix & Match:** How pieces work together
- **Real Customer Spaces:** Photos of similar aesthetic executions
- **CTAs:** "Shop Your Style" and "Get Complete Look"
- **Retake Option:** "Style evolved? Retake quiz"

**Conversion Tactics Use:**
- Style validation and naming ("You're a...")
- Complete room bundles (cohesive look)
- Lifestyle-function alignment
- Celebrity/influencer aesthetic comparisons
- Starter vs. full collection options
- Room-by-room recommendations

**Key Quiz Questions to Include:**
- **Visual Preference:** Show 4-6 room images, pick favorites
- **Color Palette:** Neutrals, bold, earthy, bright, monochrome
- **Material Attraction:** Wood, metal, glass, textiles, mixed
- **Lifestyle Needs:** Entertaining, family-friendly, work-from-home, pet owners
- **Clutter Tolerance:** Minimalist, curated, eclectic, maximalist
- **Formality Level:** Casual relaxed, polished, formal, laid-back luxe
- **Design Era:** Modern, mid-century, traditional, contemporary, vintage
- **Priority Feature:** Style, comfort, durability, storage, versatility

**Goal:** Convert indecisive shoppers into confident buyers by providing personalized style curation that eliminates overwhelm, with cohesive product recommendations and complete room bundles that simplify decorating decisions and increase average order value.`,
  },
  {
    icon: <Archive />,
    title: "Organization System Landing Pages",
    description:
      "Create a page for organization systems with measurement instructions, configuration examples, and customer transformation photos.",
    prompt: `**Build an organization system landing page that helps customers design custom solutions with measurement guidance, configuration options, and real transformation results.**

**SYSTEM FOCUS:** e.g., "Custom Closet Organization" OR "Pantry Storage System" OR "Garage Organization Kit" OR "Modular Shelving Solutions"

**CONFIGURATION TYPE:** e.g., Modular/customizable, fixed kits, expandable systems

**Target Customer:** Choose one:
- [ ] Chaos-overwhelmed (need total system overhaul)
- [ ] Space maximizers (want every inch used efficiently)
- [ ] DIY organizers (configuring themselves)

**Tone & Style:**
- Organized life coach energy
- Practical and methodical
- Transformation-focused
- "You can do this" empowerment

**CTA Guidelines:** Each CTA should:
- Reference transformation ("Organize Your Space")
- Use customization language ("Build Your System")
- Include ease messaging ("Easy to Install")
- Emphasize results ("Finally Stay Organized")

**Conversion Tactics Use:**
- Measurement calculator tools
- Configuration visualizer or templates
- Before/after transformation photos
- Space capacity calculations
- Starter vs. complete system options
- Expansion component suggestions

**Key Elements to Include:**
- **Hero Section:** System headline, organized space image, "Start Your System" CTA
- **Why You Need This:** The chaos/frustration this system solves (2-3 paragraphs)
- **How to Measure Your Space:**
  - Step-by-step measurement guide with diagrams
  - What dimensions to capture
  - Measurement worksheet downloadable
  - Video tutorial on measuring
- **System Components:** Individual pieces explained with dimensions
- **Configuration Examples (4-6):**
  - Small space setup (dimensions + products)
  - Medium space layout (dimensions + products)
  - Large space system (dimensions + products)
  - Custom configurations with visual layouts
- **Capacity Calculator:** "This system holds X items/pounds"
- **Configuration Builder Tool:** Interactive or visual guide to plan
- **Pre-Made Kits:**
  - Starter kit (essential components)
  - Complete system (full organization)
  - Expansion packs (add-ons)
- **Installation Guide:** Difficulty level, time estimate, tools needed
- **Before/After Gallery (8-12 photos):** Real customer transformations with space details
- **Customer Success Stories:** How system changed their daily routine
- **Maintenance Tips:** Keeping system organized long-term
- **Expansion Options:** How to grow system as needs change
- **Material & Durability:** What it's made of, weight capacity, longevity
- **Color/Finish Options:** Choices to match décor
- **Measurements Per Component:** Exact dimensions for planning
- **Bundle Savings:** Complete system vs. individual pieces
- **CTAs:** Hero, after measuring, with kits, after gallery (5-6 total)
- **FAQ:** 6-8 questions (How to measure? Installation difficulty? Weight capacity? Expand later? Returns? Custom sizes?)

**Goal:** Convert disorganized, overwhelmed shoppers into confident system builders by providing comprehensive measurement guidance, configuration examples, and real transformation proof that makes designing a custom organization solution feel achievable and guaranteed to work.`,
  },
  {
    icon: <Boxes />,
    title: "Project-Based Transformation Pages",
    description:
      "Build a project page with before/after galleries and a step-by-step implementation guide.",
    prompt: `**Build a project-based transformation page that inspires with before/after galleries and guides customers through step-by-step implementation.**

**PROJECT FOCUS:** e.g., "Bedroom Refresh Project" OR "Kitchen Organization Overhaul" OR "Home Office Makeover" OR "Entryway Transformation"

**PROJECT SCOPE:** e.g., Weekend project, phased approach, complete room transformation

**Target Customer:** Choose one:
- [ ] DIY enthusiasts (want to do it themselves)
- [ ] Transformation seekers (need complete change)
- [ ] Budget renovators (refresh without major reno)

**Tone & Style:**
- Motivational project coach
- Achievable and realistic
- Step-by-step clarity
- "You can transform this" confidence

**CTA Guidelines:** Each CTA should:
- Reference project completion ("Start Your Transformation")
- Use achievement language ("Complete This Project")
- Include time context ("Weekend Refresh")
- Emphasize outcome ("Get This Look")

**Conversion Tactics Use:**
- Dramatic before/after reveals
- Complete project shopping lists
- Phased budget options
- Time and difficulty estimates
- Real customer project photos
- Project bundle discounts

**Key Elements to Include:**
- **Hero Section:** Project name, stunning after image, "Shop This Project" CTA
- **Project Overview:** What's being transformed and why (2-3 paragraphs)
- **Before/After Hero Images:** Side-by-side dramatic transformation
- **Project Stats:**
  - Time required (weekend, week, phased)
  - Budget range (basic/complete/elevated)
  - Difficulty level (beginner/intermediate/advanced)
  - Skills needed
- **Complete Shopping List:** All products needed with quantities
- **Step-by-Step Implementation (6-12 steps):**
  - Step number + title
  - Time for this step
  - Products used in step
  - Detailed instructions
  - Photos showing progress
  - Pro tips per step
- **Project Timeline:** Visual showing phases and order
- **Budget Breakdown:**
  - Essential items (must-haves)
  - Complete transformation (recommended)
  - Elevated version (premium options)
- **Project Bundles:** Pre-selected product sets with savings
- **Tools & Materials Needed:** Beyond products (paint, hardware, etc.)
- **Space Requirements:** Measurements and layout considerations
- **Before/After Gallery (6-10):** Multiple angles of transformation
- **Customer Project Photos (8-12):** Real people who completed this project
- **Customization Options:** How to adapt project to your space/style
- **Common Challenges:** What to watch for and how to solve
- **Maintenance Guide:** Keeping transformation looking good
- **Phase Options:** How to break into smaller weekend projects
- **Alternative Approaches:** Different ways to achieve similar results
- **CTAs:** Hero, with shopping list, bundle sections, after gallery (5-6 total)
- **FAQ:** 5-7 questions (Really weekend doable? Renter-friendly? Skills needed? Return if doesn't work? Help available?)

**Goal:** Convert inspiration-seekers into project completers by providing comprehensive step-by-step guidance, realistic timelines, and complete shopping lists that make ambitious home transformations feel achievable, with bundle purchasing that increases order value.`,
  },
  {
    icon: <Images />,
    title: "Customer Room Galleries",
    description:
      "Create a UGC gallery where customers submit styled room photos tagged by room and aesthetic, with filtering to build confidence.",
    prompt: `**Build a customer room gallery showcasing real homes organized by room type and aesthetic to provide relatable design inspiration and purchase confidence.**

**GALLERY FOCUS:** e.g., "Real Homes, Real Style" OR "Customer Spaces" OR "See It In Your Home"

**FILTER CATEGORIES:** e.g., By room, by aesthetic, by space size, by product

**Gallery Visitor Type:** Choose one:
- [ ] Inspiration seekers (visualizing products in real spaces)
- [ ] Style-matching (want to see similar aesthetic)
- [ ] Size-conscious (need to see in similar space)

**Tone & Style:**
- Authentic and unfiltered
- Celebratory of real homes
- Inclusive of all styles/budgets
- Community showcase energy

**CTA Guidelines:** Each CTA should:
- Use community language ("Join 40,000+ Styled Homes")
- Reference relatability ("See Your Style")
- Include shopping ease ("Shop This Room")
- Encourage participation ("Share Your Space")

**Conversion Tactics Use:**
- Filter by exact room/aesthetic match
- Product tags on photos (shoppable)
- Space dimensions noted
- "Shop this room" one-click bundles
- Photo submission incentive
- Multiple angles per space

**Key Elements to Include:**
- **Hero Section:** Headline, room grid preview, filter options, "Submit Your Room" CTA
- **Filter Options:** By room type (living, bedroom, office, etc.), by aesthetic (modern, boho, traditional), by space size, by product featured, by color palette
- **Photo Grid Layout:** Hover shows room details (customer name, room type, aesthetic, products used, space size)
- **Featured Room Stories (4-6):**
  - Multiple room angles (3-5 photos)
  - Customer name + location
  - Room details (sq ft, style, challenges)
  - Products used with shoppable links
  - Design story (why they chose this)
  - Before photo if available
  - "Shop This Room" bundle CTA
- **Room Type Galleries:** Dedicated sections per room with many examples
- **Aesthetic Collections:** Curated by design style
- **Space Size Examples:** Small, medium, large room galleries
- **Product-Specific Views:** See one product in many different rooms
- **Shopping Integration:** Product tags visible on hover/click
- **Space Dimensions:** Room measurements noted for scale reference
- **Design Tips from Customers:** How they styled/arranged
- **Color Palette Tags:** Filter by dominant colors
- **Budget Level:** Affordable, mid-range, investment rooms
- **Photo Submission Section:** "Share your space & get 20% off next order"
- **Authenticity Indicators:** Real customer verification, unedited photos
- **Social Proof Stats:** "25,000+ customer rooms featured"
- **CTAs:** Top of page, with featured rooms, product tags (5-6 total)
- **FAQ:** 3-4 questions (Photo requirements? Privacy? Multiple rooms? Incentive?)

**Goal:** Convert hesitant shoppers into confident buyers by showcasing authentic customer spaces that prove how products look in real homes across different aesthetics, room sizes, and budgets - eliminating visualization anxiety and inspiring purchases through relatable, filterable design inspiration.`,
  },
  {
    icon: <Award />,
    title: "Professional Endorsement Pages",
    description:
      "Build a page featuring professional endorsements, credentials, longevity testimonials, and warranty information addressing durability.",
    prompt: `**Build a professional endorsement page that leverages industry expert credibility and durability proof to position your products as professional-grade quality.**

**ENDORSEMENT FOCUS:** e.g., "Trusted by Interior Designers" OR "Professional Quality for Your Home" OR "What Designers Actually Use"

**PROFESSIONAL TYPES:** e.g., Interior designers, architects, property stagers, contractors, organizers

**Target Audience:** Choose one:
- [ ] Quality-seekers (want professional-grade)
- [ ] Investment buyers (need durability proof)
- [ ] Design-conscious (trust expert recommendations)

**Tone & Style:**
- Credible and authoritative
- Professional expertise-driven
- Quality and longevity-focused
- "Built for professionals, made for you"

**CTA Guidelines:** Each CTA should:
- Reference professional approval ("Designer-Trusted")
- Use quality language ("Professional-Grade")
- Include durability proof ("Built to Last")
- Emphasize expertise ("Expert-Recommended")

**Conversion Tactics Use:**
- Multiple designer/architect endorsements
- Portfolio photos from professional projects
- Credentials and certifications displayed
- Longevity testimonials (years of use)
- Warranty details as quality signal
- Professional vs. consumer durability comparison

**Key Elements to Include:**
- **Hero Section:** "Trusted by 3,000+ Interior Designers" headline, professional collage, "Shop Pro-Grade" CTA
- **Professional Advisory Panel:** 4-6 featured designers/architects with photos, credentials, firm names
- **Designer Endorsements (5-8):**
  - Professional headshot + name
  - Credentials (certifications, years experience, specialties)
  - Firm name + notable projects
  - "Why I Specify This" statement (150-200 words)
  - Products they use in client projects
  - Portfolio photos featuring products
  - Client satisfaction with durability
- **Professional Project Gallery:** 8-12 photos from real design projects using products
- **Credentials Display:** NCIDQ, ASID, AIA certifications, industry memberships
- **Longevity Testimonials (6-10):** Customers who've used 5+ years with condition updates
- **Durability Features:** Construction quality, materials, weight capacity
- **Warranty Information:**
  - Coverage duration (lifetime, 10-year, etc.)
  - What's covered specifically
  - Claim process simplicity
  - Warranty as quality confidence signal
- **Professional Use Cases:** How products perform in high-traffic commercial settings
- **Material Quality:** Construction details professionals appreciate
- **Comparison Chart:** Professional-grade vs. consumer-grade quality (4-6 rows)
- **Design Firm Partnerships:** Named firms specifying your products
- **Trade Program Info:** Professional purchasing options if available
- **Industry Recognition:** Awards, trade publication features
- **CTAs:** Hero, after endorsements, with warranty, products (5-6 total)
- **FAQ:** 4-5 questions (Really professional quality? Available to consumers? Warranty coverage? Trade pricing?)

**Goal:** Convert quality-conscious consumers into confident buyers by providing overwhelming professional validation that proves your products meet design industry standards for durability and performance, backed by expert credentials, warranty confidence, and longevity proof from years of real-world use.`,
  },
  {
    icon: <Truck />,
    title: "Flash Sale Campaign Pages",
    description:
      "Build a sale page with countdown timer, automatic discount, and bestseller grid showing sale prices.",
    prompt: `**Build a flash sale campaign page that drives immediate purchases through time pressure, automatic discounts, and strategic bestseller showcasing.**

**SALE FOCUS:** e.g., "48-Hour Home Sale" OR "Weekend Only: 40% Off Furniture" OR "Flash Clearance Event"

**DISCOUNT TYPE:** e.g., "Automatic 35% off at checkout" OR "Tiered discounts" OR "Select styles 30-60% off"

**Shopper Type:** Choose one:
- [ ] Impulse buyers (time pressure converts)
- [ ] Deal waiters (holding out for sales)
- [ ] Room refreshers (sale triggers purchase)

**Tone & Style:**
- High-energy and urgent
- Exciting value celebration
- Clear about time limitation
- No-pressure but time-conscious

**CTA Guidelines:** Each CTA should:
- Emphasize time scarcity ("Ends in X Hours")
- Reference automatic savings ("35% Off Applied")
- Use urgency language ("Shop Before It Ends")
- Include countdown context

**Conversion Tactics Use:**
- Multiple countdown timer placements
- Auto-applied discount at checkout
- Bestseller social proof
- "Selling fast" live notifications
- Category spotlights (deepest discounts)
- Room bundle suggestions

**Key Elements to Include:**
- **Hero Section:** Sale headline with discount %, large countdown timer, "Shop Now" CTA
- **Countdown Timer:** Hours:minutes:seconds (large, sticky top bar, updates live)
- **Auto-Discount Banner:** "Discount automatically applied at checkout - no code needed"
- **Sale Bestsellers Grid:** 12-24 top-selling items with discount badges
- **Filter Options:** By room, by category, by discount depth, bestsellers only
- **Product Cards:** Discount %, before/after price, star rating, "Selling fast" tags
- **Room/Category Spotlights:** Featured sections (bedroom, living room, office) with sale items
- **Urgency Indicators:** "X sold in last hour" or "Low stock" badges
- **Social Proof Ticker:** Live purchase notifications ("Emma just bought this")
- **Sale Countdown Reminders:** Repeated timers mid-page and footer
- **Free Shipping Threshold:** Progress bar with suggested items
- **Bundle Suggestions:** "Complete the room" with sale items
- **Sale Terms:** Clear end time, exclusions if any, return policy
- **Limited Inventory Alerts:** Stock counters on popular items
- **CTAs:** Hero, category sections, sticky "Shop Sale" bar (always visible)
- **FAQ:** 3-4 questions (When ends? Automatic discount? Returns? Combine with other offers?)

**Goal:** Maximize sales velocity during limited window by creating urgency through countdown timers and bestseller showcasing while ensuring frictionless shopping with automatic discount application and clear sale mechanics that convert hesitant browsers into immediate buyers.`,
  },
];
